import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import Tabs from "../components/Tabs";
import AutomationHub from "./AutomationHub";
import Forms from "./Forms";
import Groups from "./Groups";
import Users from "./Users";
import Requests from "./Requests";
import MyTeam from "./MyTeam";
import HomeDashboard from "./HomeDashboard";
import Profile from "./Profile";
import Finance from "./Finance";
import Inventory from "./Inventory";
import AssetManagement from "./AssetManagement";
import HumanResources from "./HumanResources";
import OrganizationChart from "./OrganizationChart";
import RegistrationCenter from "./RegistrationCenter";
import CRM from "./CRM";
import Courses from "./Courses";
import Mural from "./Mural";
import ProjectBank from "./ProjectBank";
import AIDocuments from "./AIDocuments";
import StudentManagement, { STUDENT_PRINT_NAVIGATION_STORAGE_KEY } from "./StudentManagement";
import YoungApprenticeDashboard from "../features/youngApprentice/YoungApprenticeDashboard";
import YoungApprenticeCompanies from "../features/youngApprentice/YoungApprenticeCompanies";
import YoungApprenticeAbsences from "../features/youngApprentice/YoungApprenticeAbsences";
import YoungApprenticeInvoice from "../features/youngApprentice/YoungApprenticeInvoice";
import YoungApprenticeCompensationPage from "../features/youngApprentice/YoungApprenticeCompensationPage";
import YoungApprenticeReport from "../features/youngApprentice/YoungApprenticeReport";
import "../features/youngApprentice/YoungApprenticeBatch.css";
import "../features/youngApprentice/YoungApprenticeModal.css";
import YoungApprenticeService from "../services/YoungApprenticeService";
import { STUDENT_PRINT_NAVIGATION_VERSION } from "./studentPrintPagination";
import AttendeeManagement, { ATTENDEE_PRINT_NAVIGATION_STORAGE_KEY } from "./AttendeeManagement";
import { ATTENDEE_PRINT_NAVIGATION_VERSION } from "./attendeePrintPagination";
import { useAuth } from "../AuthContext";
import useAuthenticatedAssetUrl from "../hooks/useAuthenticatedAssetUrl";
import {
  hasPermission,
  isAdmin as isAdminPermission,
  canAccessMenuRequirement,
  PROCESS_MANAGER_PERMISSION,
  FORM_MANAGER_PERMISSION,
  COMPANY_MANAGER_PERMISSION,
  FINANCE_VIEW_PERMISSION,
  INVENTORY_VIEW_PERMISSION,
  CRM_VIEW_PERMISSION,
  ASSET_VIEW_PERMISSION,
  canManageTrash,
  REGISTRATION_MANAGE_PERMISSION,
  REGISTRATION_VIEW_PERMISSION,
  REGISTRATION_MENU_PERMISSION,
  DASHBOARD_MENU_PERMISSION,
  HUMAN_RESOURCES_MANAGE_PERMISSION,
  HUMAN_RESOURCES_VIEW_PERMISSION,
  AUTOMATION_MENU_PERMISSION,
  STUDIO_MENU_PERMISSION,
  COURSES_MENU_PERMISSION,
  COURSES_VIEW_PERMISSION,
  STUDIO_FORMS_MENU_PERMISSION,
  STUDIO_PROCESSES_MENU_PERMISSION,
  FILE_MANAGER_MENU_PERMISSION,
  AGENDA_MENU_PERMISSION,
  BUSINESS_COMPANY_MENU_PERMISSION,
  BUSINESS_FINANCE_MENU_PERMISSION,
  BUSINESS_INVENTORY_MENU_PERMISSION,
  BUSINESS_ASSET_MENU_PERMISSION,
  BUSINESS_CRM_MENU_PERMISSION,
  BUSINESS_HR_MENU_PERMISSION,
  BUSINESS_ORG_CHART_MENU_PERMISSION,
  BUSINESS_PROJECT_BANK_MENU_PERMISSION,
  PROJECT_BANK_VIEW_PERMISSION,
  STUDENT_MANAGEMENT_MENU_PERMISSION,
  ATTENDEE_MANAGEMENT_MENU_PERMISSION,
  REQUESTS_MENU_PERMISSION,
  SETTINGS_MENU_PERMISSION,
  SETTINGS_GROUPS_MENU_PERMISSION,
  SETTINGS_USERS_MENU_PERMISSION,
  SETTINGS_AUDIT_MENU_PERMISSION,
  SETTINGS_MAIL_MENU_PERMISSION,
  INTEGRATION_LOGS_PERMISSION,
  SETTINGS_USER_MANUAL_MENU_PERMISSION,
  SETTINGS_PROFILE_MENU_PERMISSION,
  SETTINGS_MY_TEAM_MENU_PERMISSION,
  TRASH_MENU_PERMISSION,
} from "../utils/permissions";
import {
  FaHome,
  FaCogs,
  FaWpforms,
  FaUsers,
  FaUser,
  FaList,
  FaCalendarAlt,
  FaCalendarTimes,
  FaSearch,
  FaPlusCircle,
  FaFolderOpen,
  FaFileAlt,
  FaBuilding,
  FaPowerOff,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaSyncAlt,
  FaTools,
  FaExclamationTriangle,
  FaHistory,
  FaTrashRestore,
  FaBook,
  FaUserFriends,
  FaSitemap,
  FaRobot,
  FaPlug,
  FaAddressBook,
  FaLayerGroup,
  FaMoneyCheckAlt,
  FaMoneyBillWave,
  FaBoxes,
  FaClipboardCheck,
  FaBriefcase,
  FaIdBadge,
  FaHandshake,
  FaGraduationCap,
  FaUserGraduate,
  FaBullhorn,
  FaProjectDiagram,
  FaChartBar,
  FaHeadset,
  FaBan,
} from "react-icons/fa";
import FileManager from "./FileManager";
import CompanyProfile from "./CompanyProfile";
import AuditLogs from "./AuditLogs";
import TrashBin from "./TrashBin";
import NotificationsBell from "../components/NotificationsBell";
import UserManual from "./UserManual";
import TechnicalSupport from "./TechnicalSupport";
import BlockedIpAddresses from "./BlockedIpAddresses";
import api from "../api";
import { subscribeDashboardRefresh } from "../utils/dashboardEvents";
import {
  ASSISTANT_ACTION_EVENT,
  ASSISTANT_ACTION_TYPES,
} from "../utils/assistantActions";
import {
  DEFAULT_REGISTRATION_PAGE_ICON,
  normalizeRegistrationPageIcon,
  resolveRegistrationPageIcon,
} from "../utils/registrationPages";

const NOTIFICATIONS_STORAGE_PREFIX = "bpm.notifications.seen.";
const NOTIFICATIONS_CACHE_PREFIX = "bpm.notifications.cache.";
const REGISTRATION_PAGES_STORAGE_KEY = "bpm.registrationCenter.pages";
const WELCOME_LOADER_SESSION_KEY = "bpm-show-welcome-loader";
const TASKS_WELCOME_LOADER_MIN_MS = 2600;
const TASKS_WELCOME_LOADER_MAX_MS = 7000;
const TASKS_WELCOME_LOADER_COMPLETE_MS = 600;

const Processes = lazy(() => import("./Processes"));
const Agenda = lazy(() => import("./Agenda"));
const NashChatWidget = lazy(() => import("../components/NashChatWidget"));

const parseRegistrationPages = (rawValue) => {
  if (!rawValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((page) => {
        if (!page) return null;
        const processId = Number(page.processId);
        if (!Number.isFinite(processId)) {
          return null;
        }

        const id =
          typeof page.id === "string" && page.id.trim()
            ? page.id.trim()
            : `registration-center-${processId}`;

        const menuLabel =
          typeof page.label === "string" && page.label.trim()
            ? page.label.trim()
            : `Processo ${processId}`;

        const tabLabel =
          typeof page.tabLabel === "string" && page.tabLabel.trim()
            ? page.tabLabel.trim()
            : `Central de Cadastros • ${menuLabel}`;

        const icon = normalizeRegistrationPageIcon(page.icon);

        return { id, processId, label: menuLabel, tabLabel, icon };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }));
  } catch (error) {
    return [];
  }
};

export default function Dashboard() {
  const { logout, user } = useAuth();
  const resolvedUserPhotoUrl = useAuthenticatedAssetUrl(user?.photoUrl);
  const isAdmin = isAdminPermission(user);
  const [collapsed, setCollapsed] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const initialHomeTabOpenedRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [forcedProfileHandled, setForcedProfileHandled] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [requestNotifications, setRequestNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const notificationsRef = useRef([]);
  const seenNotificationIdsRef = useRef(new Set());
  const notificationsLoadingRef = useRef(false);
  const [hasTeam, setHasTeam] = useState(false);
  const [youngApprenticeModuleEnabled, setYoungApprenticeModuleEnabled] = useState(false);
  const [chatReady, setChatReady] = useState(false);
  const [notificationsReady, setNotificationsReady] = useState(false);
  const [showTasksWelcomeLoader, setShowTasksWelcomeLoader] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    try {
      return window.sessionStorage.getItem(WELCOME_LOADER_SESSION_KEY) === "1";
    } catch (error) {
      return false;
    }
  });
  const [typedWelcomeMessage, setTypedWelcomeMessage] = useState("");
  const [tasksWelcomeProgress, setTasksWelcomeProgress] = useState(0);
  const [registrationPages, setRegistrationPages] = useState(() => {
    if (typeof window === "undefined") {
      return [];
    }
    return parseRegistrationPages(window.localStorage.getItem(REGISTRATION_PAGES_STORAGE_KEY));
  });
  const canManageRegistrations = useMemo(
    () => isAdmin || hasPermission(user, REGISTRATION_MANAGE_PERMISSION),
    [isAdmin, user]
  );
  const hasRegistrationMenuPermission = useMemo(
    () => hasPermission(user, REGISTRATION_MENU_PERMISSION),
    [user]
  );
  const canViewRegistrations = useMemo(
    () =>
      canManageRegistrations ||
      (hasRegistrationMenuPermission &&
        hasPermission(user, REGISTRATION_VIEW_PERMISSION)),
    [
      canManageRegistrations,
      hasRegistrationMenuPermission,
      user,
    ]
  );
  const canViewHumanResources = useMemo(
    () =>
      isAdmin ||
      hasPermission(user, HUMAN_RESOURCES_MANAGE_PERMISSION) ||
      hasPermission(user, HUMAN_RESOURCES_VIEW_PERMISSION),
    [isAdmin, user]
  );

  const notificationsCacheKey = useMemo(
    () => (user?.id ? `${NOTIFICATIONS_CACHE_PREFIX}${user.id}` : null),
    [user?.id]
  );
  const isDarkTheme = false;
  const firstName = useMemo(() => {
    const candidate = user?.name || user?.email || "usuário";
    return String(candidate).trim().split(/\s+/)[0] || "usuário";
  }, [user?.email, user?.name]);
  const welcomeMessage = `Olá ${firstName}, seja bem-vindo ao Nash iBPM!`;

  useEffect(() => {
    if (!showTasksWelcomeLoader || typeof window === "undefined") {
      return undefined;
    }

    setTypedWelcomeMessage("");
    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setTypedWelcomeMessage(welcomeMessage.slice(0, index));
      if (index >= welcomeMessage.length) {
        window.clearInterval(timer);
      }
    }, 58);

    return () => window.clearInterval(timer);
  }, [showTasksWelcomeLoader, welcomeMessage]);

  const persistNotificationsCache = useCallback(
    (items) => {
      if (!notificationsCacheKey || typeof window === "undefined") {
        return;
      }
      try {
        window.localStorage.setItem(
          notificationsCacheKey,
          JSON.stringify({ items })
        );
      } catch (error) {
        // ignore cache persistence failures silently
      }
    },
    [notificationsCacheKey]
  );

  const applySeenFlags = useCallback((items) => {
    if (!Array.isArray(items)) {
      return [];
    }
    return items.map((item) =>
      item && Number.isFinite(Number(item.id))
        ? { ...item, isNew: !seenNotificationIdsRef.current.has(Number(item.id)) }
        : null
    ).filter(Boolean);
  }, []);

  const updateNotificationState = useCallback(
    (items, shouldCache = true) => {
      const normalized = applySeenFlags(items);
      notificationsRef.current = normalized;
      setRequestNotifications(normalized);
      setUnreadNotifications(normalized.filter((item) => item.isNew).length);
      if (shouldCache) {
        persistNotificationsCache(normalized);
      }
    },
    [applySeenFlags, persistNotificationsCache]
  );

  const loadCachedNotifications = useCallback(() => {
    if (!notificationsCacheKey || typeof window === "undefined") {
      return false;
    }
    try {
      const cached = window.localStorage.getItem(notificationsCacheKey);
      if (!cached) {
        return false;
      }
      const parsed = JSON.parse(cached);
      const cachedItems = Array.isArray(parsed?.items) ? parsed.items : [];
      if (!cachedItems.length) {
        return false;
      }
      updateNotificationState(cachedItems, false);
      return true;
    } catch (error) {
      return false;
    }
  }, [notificationsCacheKey, updateNotificationState]);


  useEffect(() => {
    if (!showTasksWelcomeLoader || typeof window === "undefined") {
      return undefined;
    }

    try {
      window.sessionStorage.removeItem(WELCOME_LOADER_SESSION_KEY);
    } catch (error) {
      // ignore storage failures silently
    }

    const startedAt = Date.now();
    let finishTimer = null;
    let maxTimer = null;
    let progressTimer = null;
    let tasksReady = false;
    let finishing = false;

    setTasksWelcomeProgress(0);
    progressTimer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const baseProgress = Math.min((elapsed / TASKS_WELCOME_LOADER_MAX_MS) * 90, 90);
      setTasksWelcomeProgress((current) => (current >= 100 ? current : Math.max(current, baseProgress)));
    }, 120);

    const finishLoader = () => {
      if (finishing) {
        return;
      }
      finishing = true;
      if (progressTimer) {
        window.clearInterval(progressTimer);
      }
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(TASKS_WELCOME_LOADER_MIN_MS - elapsed, 0);
      const delay = remaining + TASKS_WELCOME_LOADER_COMPLETE_MS;
      setTasksWelcomeProgress(100);
      if (finishTimer) {
        window.clearTimeout(finishTimer);
      }
      finishTimer = window.setTimeout(() => {
        setShowTasksWelcomeLoader(false);
      }, delay);
    };

    const onTasksPriorityReady = () => {
      tasksReady = true;
      finishLoader();
    };

    window.addEventListener("bpm:home-tasks-priority-ready", onTasksPriorityReady);
    maxTimer = window.setTimeout(() => {
      if (!tasksReady) {
        finishLoader();
      }
    }, TASKS_WELCOME_LOADER_MAX_MS);

    return () => {
      window.removeEventListener("bpm:home-tasks-priority-ready", onTasksPriorityReady);
      if (finishTimer) {
        window.clearTimeout(finishTimer);
      }
      if (maxTimer) {
        window.clearTimeout(maxTimer);
      }
      if (progressTimer) {
        window.clearInterval(progressTimer);
      }
    };
  }, [showTasksWelcomeLoader]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.key !== REGISTRATION_PAGES_STORAGE_KEY) {
        return;
      }
      setRegistrationPages(parseRegistrationPages(event.newValue));
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.documentElement.removeAttribute("data-theme");
    document.documentElement.style.colorScheme = "light";
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("bpm.theme.preference");
    }
  }, []);

  useEffect(() => {
    let activated = false;

    const activateNotifications = () => {
      if (activated) {
        return;
      }
      activated = true;
      setNotificationsReady(true);
    };

    const onTasksPriorityReady = () => {
      activateNotifications();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("bpm:home-tasks-priority-ready", onTasksPriorityReady);
    }

    const timeout =
      typeof window !== "undefined"
        ? window.setTimeout(() => activateNotifications(), 4500)
        : null;

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("bpm:home-tasks-priority-ready", onTasksPriorityReady);
      }
      if (timeout) {
        window.clearTimeout(timeout);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(
        REGISTRATION_PAGES_STORAGE_KEY,
        JSON.stringify(registrationPages),
      );
    } catch (error) {
      // ignore storage errors silently
    }
  }, [registrationPages]);

  useEffect(() => {
    let activated = false;

    const activateChat = () => {
      if (activated) {
        return;
      }
      activated = true;
      setChatReady(true);
    };

    const onTasksPriorityReady = () => {
      activateChat();
    };

    const idleCallback =
      typeof window !== "undefined" && typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback
        : null;

    const cancelIdle =
      typeof window !== "undefined" && typeof window.cancelIdleCallback === "function"
        ? window.cancelIdleCallback
        : null;

    if (typeof window !== "undefined") {
      window.addEventListener("bpm:home-tasks-priority-ready", onTasksPriorityReady);
    }

    const handle = idleCallback
      ? idleCallback(() => activateChat(), { timeout: 3000 })
      : window.setTimeout(() => activateChat(), 2200);

    const hardTimeout =
      typeof window !== "undefined"
        ? window.setTimeout(() => activateChat(), 6000)
        : null;

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("bpm:home-tasks-priority-ready", onTasksPriorityReady);
      }
      if (idleCallback && cancelIdle && handle) {
        cancelIdle(handle);
      } else if (handle) {
        window.clearTimeout(handle);
      }
      if (hardTimeout) {
        window.clearTimeout(hardTimeout);
      }
    };
  }, []);

  const refreshNotifications = useCallback(async () => {
    if (!user?.id) {
      notificationsRef.current = [];
      setRequestNotifications([]);
      setUnreadNotifications(0);
      return;
    }

    if (notificationsLoadingRef.current) {
      return;
    }

    try {
      notificationsLoadingRef.current = true;
      const response = await api.get("/processes/instances/open");
      const list = Array.isArray(response.data) ? response.data : [];
      const relevant = list.filter(
        (instance) =>
          instance &&
          instance.status === "ACTIVE" &&
          (instance.isCurrentAssignee || instance.isPotentialResponsible)
      );

      const mapped = relevant
        .map((instance) => {
          const numericId = Number(instance.id);
          if (!Number.isFinite(numericId)) {
            return null;
          }
          const stepKey = String(
            instance.currentStep !== undefined && instance.currentStep !== null
              ? instance.currentStep
              : ""
          );
          const rawStepName = instance.process?.stepNames?.[stepKey];
          const normalizedStepName =
            typeof rawStepName === "string" && rawStepName.trim()
              ? rawStepName.trim()
              : typeof instance.currentStep === "number"
              ? `Etapa ${instance.currentStep + 1}`
              : null;
          const history = Array.isArray(instance.stepHistory)
            ? instance.stepHistory
            : [];
          const lastHistory = history.length ? history[history.length - 1] : null;
          const receivedAt = lastHistory?.date || instance.startedAt || null;
          const requesterName =
            typeof instance.startedBy?.name === "string" && instance.startedBy.name.trim()
              ? instance.startedBy.name.trim()
              : typeof instance.startedBy?.email === "string" && instance.startedBy.email.trim()
              ? instance.startedBy.email.trim()
              : null;
          const highlightLabel =
            typeof instance.dashboardHighlight?.label === "string" &&
            instance.dashboardHighlight.label.trim()
              ? instance.dashboardHighlight.label.trim()
              : null;
          const highlightValue =
            typeof instance.dashboardHighlight?.value === "string" &&
            instance.dashboardHighlight.value.trim()
              ? instance.dashboardHighlight.value.trim()
              : null;
          return {
            id: numericId,
            processName:
              typeof instance.process?.name === "string" && instance.process.name.trim()
                ? instance.process.name.trim()
                : null,
            stepName: normalizedStepName,
            requester: requesterName,
            receivedAt,
            isNew: !seenNotificationIdsRef.current.has(numericId),
            highlightLabel,
            highlightValue,
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          const dateA = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
          const dateB = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
          if (dateA === dateB) {
            return b.id - a.id;
          }
          return dateB - dateA;
        });

      updateNotificationState(mapped);
    } catch (error) {
      notificationsRef.current = [];
      setRequestNotifications([]);
      setUnreadNotifications(0);
    }
    notificationsLoadingRef.current = false;
  }, [updateNotificationState, user?.id]);

  const handleCreateRegistrationPage = useCallback(
    (process) => {
      const result = { created: false, page: null, pages: registrationPages };
      if (!canManageRegistrations) {
        return result;
      }
      if (!process || process.id === undefined || process.id === null) {
        return result;
      }

      const processId = Number(process.id);
      if (!Number.isFinite(processId)) {
        return result;
      }

      const baseName =
        typeof process.name === "string" && process.name.trim()
          ? process.name.trim()
          : `Processo ${processId}`;

      const newPage = {
        id: `registration-center-${processId}`,
        processId,
        label: baseName,
        tabLabel: `Central de Cadastros • ${baseName}`,
        icon: DEFAULT_REGISTRATION_PAGE_ICON,
      };

      const existing = registrationPages.find(
        (page) => page.processId === processId,
      );

      if (existing) {
        return { created: false, page: existing, pages: registrationPages };
      }

      const nextPages = [...registrationPages, newPage].sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }),
      );
      setRegistrationPages(nextPages);
      return { created: true, page: newPage, pages: nextPages };
    },
    [registrationPages, canManageRegistrations],
  );

  const handleUpdateRegistrationPage = useCallback((processId, updates) => {
    if (!Number.isFinite(processId) || !updates) {
      return null;
    }
    let updatedPage = null;
    setRegistrationPages((prev) => {
      const index = prev.findIndex((page) => page.processId === processId);
      if (index === -1) {
        return prev;
      }
      const next = [...prev];
      const current = next[index];
      const payload = { ...current };

      if (typeof updates.label === "string") {
        const trimmed = updates.label.trim();
        if (trimmed) {
          payload.label = trimmed;
          if (!updates.tabLabel) {
            payload.tabLabel = `Central de Cadastros • ${trimmed}`;
          }
        }
      }

      if (typeof updates.tabLabel === "string") {
        const trimmedTab = updates.tabLabel.trim();
        if (trimmedTab) {
          payload.tabLabel = trimmedTab;
        }
      }

      if (typeof updates.icon === "string") {
        payload.icon = normalizeRegistrationPageIcon(updates.icon);
      }

      updatedPage = payload;
      next[index] = payload;
      return next
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }));
    });
    return updatedPage;
  }, []);

  const handleDeleteRegistrationPage = useCallback((processId) => {
    if (!Number.isFinite(processId)) {
      return false;
    }
    let removed = false;
    setRegistrationPages((prev) => {
      const next = prev.filter((page) => page.processId !== processId);
      removed = next.length !== prev.length;
      return next;
    });
    return removed;
  }, []);

  useEffect(() => {
    seenNotificationIdsRef.current = new Set();
    if (!user?.id) {
      notificationsRef.current = [];
      setRequestNotifications([]);
      setUnreadNotifications(0);
      return;
    }

    if (!notificationsReady) {
      return;
    }

    if (typeof window !== "undefined") {
      try {
        const stored = window.localStorage.getItem(
          `${NOTIFICATIONS_STORAGE_PREFIX}${user.id}`
        );
        if (stored) {
          const parsed = JSON.parse(stored);
          const seenIds = Array.isArray(parsed?.seenIds) ? parsed.seenIds : [];
          const normalized = seenIds
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value));
          seenNotificationIdsRef.current = new Set(normalized);
        }
      } catch (error) {
        seenNotificationIdsRef.current = new Set();
      }
    }

    loadCachedNotifications();
    refreshNotifications();
  }, [loadCachedNotifications, notificationsReady, refreshNotifications, user?.id]);

  useEffect(() => {
    setHasTeam(Boolean(user?.id));
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;
    const loadYoungApprenticeModule = () => YoungApprenticeService.moduleStatus()
      .then((data) => { if (mounted) setYoungApprenticeModuleEnabled(Boolean(data?.enabled)); })
      .catch(() => { if (mounted) setYoungApprenticeModuleEnabled(false); });
    loadYoungApprenticeModule();
    const handleModuleChange = (event) => setYoungApprenticeModuleEnabled(Boolean(event?.detail?.enabled));
    window.addEventListener('bpm:young-apprentice-module-changed', handleModuleChange);
    return () => { mounted = false; window.removeEventListener('bpm:young-apprentice-module-changed', handleModuleChange); };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !notificationsReady) return undefined;
    const interval = setInterval(() => {
      refreshNotifications();
    }, 20000);
    return () => {
      clearInterval(interval);
    };
  }, [notificationsReady, refreshNotifications, user?.id]);

  const persistSeenNotifications = useCallback(() => {
    if (typeof window === "undefined" || !user?.id) return;
    try {
      window.localStorage.setItem(
        `${NOTIFICATIONS_STORAGE_PREFIX}${user.id}`,
        JSON.stringify({ seenIds: Array.from(seenNotificationIdsRef.current) })
      );
    } catch (error) {
      // Ignora falhas no armazenamento local
    }
  }, [user?.id]);

  const markNotificationsAsRead = useCallback(
    (ids) => {
      if (!Array.isArray(ids) || !ids.length) return;
      const updated = new Set(seenNotificationIdsRef.current);
      let changed = false;
      ids.forEach((id) => {
        const numericId = Number(id);
        if (!Number.isFinite(numericId)) return;
        if (!updated.has(numericId)) {
          updated.add(numericId);
          changed = true;
        }
      });
      if (!changed) return;

      seenNotificationIdsRef.current = updated;
      const next = applySeenFlags(notificationsRef.current);
      notificationsRef.current = next;
      setRequestNotifications(next);
      setUnreadNotifications(next.filter((item) => item.isNew).length);
      persistNotificationsCache(next);
      persistSeenNotifications();
    },
    [applySeenFlags, persistNotificationsCache, persistSeenNotifications]
  );

  useEffect(() => {
    if (activeTab !== "requests") return;
    const unreadIds = notificationsRef.current
      .filter((item) => item.isNew)
      .map((item) => item.id);
    if (unreadIds.length) {
      markNotificationsAsRead(unreadIds);
    }
  }, [activeTab, markNotificationsAsRead]);

  const refreshTab = useCallback((id) => {
    if (!id) return;
    setTabs((prevTabs) => {
      let updated = false;
      const next = prevTabs.map((tab) => {
        if (tab.id !== id) {
          return tab;
        }
        updated = true;
        return {
          ...tab,
          props: { ...(tab.props || {}), refreshKey: Date.now() },
        };
      });
      return updated ? next : prevTabs;
    });
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeDashboardRefresh(() => {
      refreshNotifications();
      refreshTab("home");
      refreshTab("requests");
    });
    return unsubscribe;
  }, [refreshNotifications, refreshTab]);

  const openTab = useCallback(
    (id, title, Component, props = {}) => {
      const passwordChangeRequired = Boolean(user?.mustChangePassword);
      const targetId = passwordChangeRequired && id !== "profile" ? "profile" : id;
      const targetTitle = passwordChangeRequired && id !== "profile" ? "Perfil" : title;
      const TargetComponent = passwordChangeRequired && id !== "profile" ? Profile : Component;
      const targetProps =
        passwordChangeRequired && id !== "profile"
          ? { forcePasswordChange: true }
          : props;

      setTabs((t) => {
        const refreshKey = Date.now();
        const existingIndex = t.findIndex((tab) => tab.id === targetId);
        if (existingIndex !== -1) {
          return t.map((tab, index) =>
            index === existingIndex
              ? {
                  ...tab,
                  title: targetTitle,
                  Component: TargetComponent,
                  props: { ...(tab.props || {}), ...targetProps, refreshKey },
                }
              : tab
          );
        }
        return [
          ...t,
          {
            id: targetId,
            title: targetTitle,
            Component: TargetComponent,
            props: { ...targetProps, refreshKey },
          },
        ];
      });
      setActiveTab(targetId);
      if (isMobile) {
        setMobileMenuOpen(false);
      }
    },
    [isMobile, user?.mustChangePassword]
  );

  useEffect(() => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) => {
        if (tab.Component !== RegistrationCenter) {
          return tab;
        }

        const lockedProcessId = tab.props?.lockedProcessId ?? null;
        let title = tab.title;
        const updatedProps = {
          ...(tab.props || {}),
          registrationPages,
          onCreateRegistrationPage: handleCreateRegistrationPage,
          onUpdateRegistrationPage: handleUpdateRegistrationPage,
          onDeleteRegistrationPage: handleDeleteRegistrationPage,
          canManageRegistrations,
          openTab,
        };

        if (lockedProcessId) {
          const page = registrationPages.find(
            (item) => item.processId === lockedProcessId,
          );
          if (page) {
            title = page.tabLabel || page.label;
            updatedProps.registrationPageName = page.label;
            updatedProps.tabLabel = page.tabLabel || page.label;
          }
        }

        return {
          ...tab,
          title,
          props: updatedProps,
        };
      })
    );
  }, [
    registrationPages,
    handleCreateRegistrationPage,
    handleUpdateRegistrationPage,
    handleDeleteRegistrationPage,
    openTab,
    canManageRegistrations,
  ]);

  const handleSelectTab = useCallback(
    (id) => {
      if (!id) return;
      const targetId = user?.mustChangePassword && id !== "profile" ? "profile" : id;
      refreshTab(targetId);
      setActiveTab(targetId);
    },
    [refreshTab, user?.mustChangePassword]
  );

  const menuItems = useMemo(() => {
    const registrationCenterBaseProps = {
      openTab,
      registrationPages,
      onCreateRegistrationPage: handleCreateRegistrationPage,
      onUpdateRegistrationPage: handleUpdateRegistrationPage,
      onDeleteRegistrationPage: handleDeleteRegistrationPage,
      canManageRegistrations,
    };

    const registrationCenterChildren = [
      {
        id: "registration-center",
        label: "Cadastros BPM",
        tabLabel: "Cadastros BPM",
        icon: FaAddressBook,
        component: RegistrationCenter,
        props: registrationCenterBaseProps,
        requiredPermissions: [
          REGISTRATION_MENU_PERMISSION,
          REGISTRATION_VIEW_PERMISSION,
        ],
      },
      ...registrationPages.map((page) => ({
        id: page.id,
        label: page.label,
        tabLabel: page.tabLabel || page.label,
        icon: resolveRegistrationPageIcon(page.icon),
        component: RegistrationCenter,
        props: {
          openTab,
          registrationPages,
          onCreateRegistrationPage: handleCreateRegistrationPage,
          onUpdateRegistrationPage: handleUpdateRegistrationPage,
          onDeleteRegistrationPage: handleDeleteRegistrationPage,
          canManageRegistrations,
          initialProcessId: page.processId,
          lockedProcessId: page.processId,
          registrationPageName: page.label,
        },
        requiredPermissions: [
          REGISTRATION_MENU_PERMISSION,
          REGISTRATION_VIEW_PERMISSION,
        ],
      })),
    ];

    const registrationCenterItem =
      registrationPages.length > 0
        ? {
            id: "registration-center-group",
            label: "Cadastros BPM",
            icon: FaAddressBook,
            requiredPermissions: [
              REGISTRATION_MENU_PERMISSION,
              REGISTRATION_VIEW_PERMISSION,
            ],
            children: registrationCenterChildren,
          }
        : {
            id: "registration-center",
            label: "Cadastros BPM",
            tabLabel: "Cadastros BPM",
            icon: FaAddressBook,
            component: RegistrationCenter,
            props: registrationCenterBaseProps,
            requiredPermissions: [
              REGISTRATION_MENU_PERMISSION,
              REGISTRATION_VIEW_PERMISSION,
            ],
          };

    const items = [
      {
        id: "home",
        label: "Dashboard",
        icon: FaHome,
        component: HomeDashboard,
        props: { openTab },
        requiredPermission: DASHBOARD_MENU_PERMISSION,
      },
      {
        id: "mural",
        label: "Mural",
        icon: FaBullhorn,
        component: Mural,
        props: { openTab },
        alwaysVisible: true,
      },
      ...(canViewRegistrations ? [registrationCenterItem] : []),
      ...(youngApprenticeModuleEnabled
        ? [
          {
            id: "young-apprentice-management",
            label: "Gestão de Aprendiz",
            icon: FaBriefcase,
            children: [
              { id: "young-apprentice-dashboard", label: "Dashboard", icon: FaChartBar, component: YoungApprenticeDashboard, requiredPermission: "VISUALIZAR_DASHBOARDS_JOVEM_APRENDIZ" },
              { id: "student-management", label: "Atendidos", icon: FaUserGraduate, component: StudentManagement, props: { youngApprenticeMode: true, youngApprenticeCanManage: canAccessMenuRequirement(user, "GERENCIAR_MUNDO_DO_TRABALHO_JOVEM_APRENDIZ"), youngApprenticeCanViewStudents: canAccessMenuRequirement(user, "VISUALIZAR_ALUNOS_JOVEM_APRENDIZ"), youngApprenticeCanEditStudents: canAccessMenuRequirement(user, "GERENCIAR_ALUNOS_JOVEM_APRENDIZ"), youngApprenticeCanViewDashboard: canAccessMenuRequirement(user, "VISUALIZAR_DASHBOARDS_JOVEM_APRENDIZ") }, requiredAnyPermissions: ["VISUALIZAR_DASHBOARDS_JOVEM_APRENDIZ", "VISUALIZAR_ALUNOS_JOVEM_APRENDIZ", "GERENCIAR_ALUNOS_JOVEM_APRENDIZ", "VISUALIZAR_MUNDO_DO_TRABALHO_JOVEM_APRENDIZ", "GERENCIAR_MUNDO_DO_TRABALHO_JOVEM_APRENDIZ"] },
              { id: "young-apprentice-companies", label: "Empresas", icon: FaBuilding, component: YoungApprenticeCompanies, props: { canManage: canAccessMenuRequirement(user, "GERENCIAR_EMPRESAS_JOVEM_APRENDIZ") }, requiredPermission: "VISUALIZAR_EMPRESAS_JOVEM_APRENDIZ" },
              { id: "young-apprentice-absences", label: "Registro de Faltas", icon: FaCalendarTimes, component: YoungApprenticeAbsences, props: { canManage: canAccessMenuRequirement(user, "GERENCIAR_FALTAS_JOVEM_APRENDIZ") }, requiredPermission: "VISUALIZAR_FALTAS_JOVEM_APRENDIZ" },
              { id: "young-apprentice-invoice", label: "Nota Fatura", icon: FaMoneyCheckAlt, component: YoungApprenticeInvoice, props: { canManage: canAccessMenuRequirement(user, "GERENCIAR_NOTA_FATURA_JOVEM_APRENDIZ") }, requiredPermission: "VISUALIZAR_NOTA_FATURA_JOVEM_APRENDIZ" },
              { id: "young-apprentice-report", label: "Relatório", icon: FaClipboardCheck, component: YoungApprenticeReport, requiredPermission: "VISUALIZAR_RELATORIO_MUNDO_DO_TRABALHO_JOVEM_APRENDIZ" },
              { id: "young-apprentice-compensation", label: "Cadastros", icon: FaMoneyBillWave, component: YoungApprenticeCompensationPage, requiredPermission: "VISUALIZAR_CADASTROS_JOVEM_APRENDIZ" },
            ],
          },
        ]
        : [{
            id: "student-management",
            label: "Gestão de Alunos",
            icon: FaUserGraduate,
            component: StudentManagement,
            requiredPermission: STUDENT_MANAGEMENT_MENU_PERMISSION,
          }]),
      {
        id: "attendee-management",
        label: "Gestão de Atendidos",
        icon: FaUserGraduate,
        component: AttendeeManagement,
        requiredPermission: ATTENDEE_MANAGEMENT_MENU_PERMISSION,
      },
      {
        id: "studio",
        label: "Studio",
        icon: FaLayerGroup,
        requiredPermission: STUDIO_MENU_PERMISSION,
        children: [
          {
            id: "project-bank",
            label: "Banco de Projetos",
            icon: FaProjectDiagram,
            component: ProjectBank,
            requiredPermissions: [
              BUSINESS_PROJECT_BANK_MENU_PERMISSION,
              PROJECT_BANK_VIEW_PERMISSION,
            ],
          },
          {
            id: "forms",
            label: "Formulários",
            icon: FaWpforms,
            component: Forms,
            props: { openTab },
            requiredPermissions: [
              STUDIO_FORMS_MENU_PERMISSION,
              FORM_MANAGER_PERMISSION,
            ],
          },
          {
            id: "processes",
            label: "Processos",
            icon: FaCogs,
            component: Processes,
            props: { openTab },
            requiredPermissions: [
              STUDIO_PROCESSES_MENU_PERMISSION,
              PROCESS_MANAGER_PERMISSION,
            ],
          },
        ],
      },
      {
        id: "agenda",
        label: "Minha Agenda",
        icon: FaCalendarAlt,
        component: Agenda,
        props: { openTab },
        requiredPermission: AGENDA_MENU_PERMISSION,
      },
      {
        id: "business",
        label: "Meu Negócio",
        icon: FaBriefcase,
        // O contêiner aparece quando ao menos um módulo filho está autorizado.
        // Cada módulo continua exigindo suas próprias permissões de menu e acesso.
        children: [
          {
            id: "file-manager",
            label: "Arquivos",
            icon: FaFolderOpen,
            component: FileManager,
            props: { openTab },
            requiredPermission: FILE_MANAGER_MENU_PERMISSION,
          },
          {
            id: "crm",
            label: "CRM",
            icon: FaHandshake,
            component: CRM,
            requiredPermissions: [
              BUSINESS_CRM_MENU_PERMISSION,
              CRM_VIEW_PERMISSION,
            ],
          },
          {
            id: "courses",
            label: "Cursos",
            icon: FaGraduationCap,
            component: Courses,
            props: { openTab },
            requiredPermissions: [
              COURSES_MENU_PERMISSION,
              COURSES_VIEW_PERMISSION,
            ],
          },
          {
            id: "company",
            label: "Dados Cadastrais",
            icon: FaBuilding,
            component: CompanyProfile,
            requiredPermissions: [
              BUSINESS_COMPANY_MENU_PERMISSION,
              COMPANY_MANAGER_PERMISSION,
            ],
          },
          {
            id: "company-documents",
            label: "Documentação",
            icon: FaFileAlt,
            component: CompanyProfile,
            props: { section: "documents" },
            requiredPermissions: [
              BUSINESS_COMPANY_MENU_PERMISSION,
              COMPANY_MANAGER_PERMISSION,
            ],
          },
          {
            id: "ai-documents",
            label: "Documentos IA",
            icon: FaFileAlt,
            component: AIDocuments,
            alwaysVisible: true,
          },
          {
            id: "inventory",
            label: "Estoque",
            icon: FaBoxes,
            component: Inventory,
            props: { openTab },
            requiredPermissions: [
              BUSINESS_INVENTORY_MENU_PERMISSION,
              INVENTORY_VIEW_PERMISSION,
            ],
          },
          {
            id: "finance",
            label: "Financeiro",
            icon: FaMoneyCheckAlt,
            component: Finance,
            props: { openTab },
            requiredPermissions: [
              BUSINESS_FINANCE_MENU_PERMISSION,
              FINANCE_VIEW_PERMISSION,
            ],
          },
          {
            id: "organization-chart",
            label: "Organograma",
            icon: FaSitemap,
            component: OrganizationChart,
            props: { openTab },
            requiredPermission: BUSINESS_ORG_CHART_MENU_PERMISSION,
          },
          {
            id: "asset-management",
            label: "Patrimônio",
            icon: FaClipboardCheck,
            component: AssetManagement,
            requiredPermissions: [
              BUSINESS_ASSET_MENU_PERMISSION,
              ASSET_VIEW_PERMISSION,
            ],
          },
          ...(canViewHumanResources
            ? [
                {
                  id: "human-resources",
                  label: "Recursos Humanos",
                  icon: FaIdBadge,
                  component: HumanResources,
                  props: { openTab },
                  requiredPermissions: [
                    BUSINESS_HR_MENU_PERMISSION,
                    HUMAN_RESOURCES_VIEW_PERMISSION,
                  ],
                },
              ]
            : []),
        ],
      },
      {
        id: "settings",
        label: "Configurações",
        icon: FaTools,
        requiredAnyPermissions: [
          SETTINGS_MENU_PERMISSION,
          AUTOMATION_MENU_PERMISSION,
          PROCESS_MANAGER_PERMISSION,
          SETTINGS_MAIL_MENU_PERMISSION,
          INTEGRATION_LOGS_PERMISSION,
        ],
        children: (() => {
          const children = [
            {
              id: "automation-hub",
              label: "Integrações",
              icon: FaPlug,
              component: AutomationHub,
              props: { openTab },
              requiredAnyPermissions: [
                AUTOMATION_MENU_PERMISSION,
                PROCESS_MANAGER_PERMISSION,
                SETTINGS_MAIL_MENU_PERMISSION,
                INTEGRATION_LOGS_PERMISSION,
              ],
            },
            {
              id: "requests",
              label: "Minhas solicitações",
              icon: FaList,
              component: Requests,
              props: { openTab },
              requiredPermission: REQUESTS_MENU_PERMISSION,
            },
            ...(hasTeam
              ? [
                  {
                    id: "my-team",
                    label: "Minha equipe",
                    icon: FaUserFriends,
                    component: MyTeam,
                    props: { openTab },
                    requiredPermission: SETTINGS_MY_TEAM_MENU_PERMISSION,
                  },
                ]
              : []),

            ...(user?.isMaster
              ? [
                  {
                    id: "blocked-ip-addresses",
                    label: "Endereços bloqueados",
                    icon: FaBan,
                    component: BlockedIpAddresses,
                    alwaysVisible: true,
                  },
                ]
              : []),
            {
              id: "groups",
              label: "Grupo/Permissões",
              icon: FaUsers,
              component: Groups,
              requiredPermission: SETTINGS_GROUPS_MENU_PERMISSION,
            },
            {
              id: "users",
              label: "Usuários",
              icon: FaUser,
              component: Users,
              requiredPermission: SETTINGS_USERS_MENU_PERMISSION,
            },
            {
              id: "audit",
              label: "Auditoria",
              icon: FaHistory,
              component: AuditLogs,
              requiredPermission: SETTINGS_AUDIT_MENU_PERMISSION,
            },
            {
              id: "user-manual",
              label: "Manual do usuário",
              icon: FaBook,
              component: UserManual,
              requiredPermission: SETTINGS_USER_MANUAL_MENU_PERMISSION,
            },
            {
              id: "profile",
              label: "Meu Perfil",
              icon: FaUser,
              component: Profile,
              props: { forcePasswordChange: Boolean(user?.mustChangePassword) },
              requiredPermission: SETTINGS_PROFILE_MENU_PERMISSION,
            },
          ];

          if (canManageTrash(user)) {
            children.push({
              id: "trash",
              label: "Lixeira",
              icon: FaTrashRestore,
              component: TrashBin,
              props: { openTab },
              requiredPermission: TRASH_MENU_PERMISSION,
            });
          }

          const sortedChildren = children.sort((a, b) =>
            a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
          );

          return [
            ...sortedChildren,
            {
              id: "technical-support",
              label: "Suporte Técnico",
              icon: FaHeadset,
              component: TechnicalSupport,
              requiredPermission: SETTINGS_MENU_PERMISSION,
            },
          ];
        })(),
      },
    ];

    const filterItems = (list) =>
      list
        .map((item) => {
          const requiredPermissions = [
            ...(Array.isArray(item.requiredPermissions)
              ? item.requiredPermissions
              : []),
            ...(item.requiredPermission ? [item.requiredPermission] : []),
          ];
          const requiredAnyPermissions = Array.isArray(item.requiredAnyPermissions)
            ? item.requiredAnyPermissions
            : [];

          const hasRequiredPermission =
            item.alwaysVisible ||
            requiredPermissions.length === 0 ||
            requiredPermissions.every((permission) =>
              canAccessMenuRequirement(user, permission),
            );
          const hasAnyRequiredPermission =
            item.alwaysVisible ||
            requiredAnyPermissions.length === 0 ||
            requiredAnyPermissions.some((permission) =>
              canAccessMenuRequirement(user, permission),
            );

          const hasAccess =
            (!item.adminOnly || isAdmin) &&
            hasRequiredPermission &&
            hasAnyRequiredPermission;

          if (item.children) {
            if (!hasAccess) {
              return null;
            }

            const children = filterItems(item.children);
            if (!children.length) {
              return null;
            }
            return { ...item, children };
          }

          return hasAccess ? item : null;
        })
        .filter(Boolean);

    return filterItems(items);
  }, [
    handleCreateRegistrationPage,
    handleUpdateRegistrationPage,
    handleDeleteRegistrationPage,
    hasTeam,
    isAdmin,
    openTab,
    registrationPages,
    canManageRegistrations,
    canViewRegistrations,
    canViewHumanResources,
    youngApprenticeModuleEnabled,
    user,
  ]);


  useEffect(() => {
    const flattenMenu = (items, parentPath = []) =>
      items.flatMap((item) => {
        if (!item) return [];
        const path = [...parentPath, item.label].filter(Boolean);
        const current = item.component || item.id ? [{ id: item.id, label: item.label, path: path.join(" > ") }] : [];
        const children = Array.isArray(item.children) ? flattenMenu(item.children, path) : [];
        return [...current, ...children];
      });
    if (typeof window !== "undefined") {
      window.__NASH_MENU_CONTEXT__ = {
        updatedAt: Date.now(),
        items: flattenMenu(menuItems),
      };
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.__NASH_MENU_CONTEXT__;
      }
    };
  }, [menuItems]);

  const menuItemLookup = useMemo(() => {
    const lookup = {};
    const registerItems = (items) => {
      items.forEach((item) => {
        if (!item) {
          return;
        }
        if (item.id && item.component) {
          lookup[item.id] = item;
        }
        if (Array.isArray(item.children)) {
          registerItems(item.children);
        }
      });
    };
    registerItems(menuItems);
    return lookup;
  }, [menuItems]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const openTerminationInvoice = (event) => {
      const employmentId = Number(event?.detail?.employmentId || 0);
      if (!employmentId) return;
      const item = menuItemLookup["young-apprentice-invoice"];
      if (!item?.component) return;
      openTab(item.id, item.label, item.component, {
        ...(item.props || {}),
        terminationEmploymentId: employmentId,
      });
    };
    window.addEventListener("bpm:open-young-apprentice-termination-invoice", openTerminationInvoice);
    return () => window.removeEventListener("bpm:open-young-apprentice-termination-invoice", openTerminationInvoice);
  }, [menuItemLookup, openTab]);

  const [openSubmenus, setOpenSubmenus] = useState({});
  const submenuTimersRef = useRef({});

  useEffect(() => {
    if (searchTerm) {
      const expanded = {};
      const expandAll = (items) => {
        items.forEach((item) => {
          if (item.children) {
            expanded[item.id] = true;
            expandAll(item.children);
          }
        });
      };
      expandAll(menuItems);
      setOpenSubmenus(expanded);
    } else {
      setOpenSubmenus({});
    }
  }, [menuItems, searchTerm]);

  const submenuParents = useMemo(() => {
    const parentsMap = {};
    const registerParents = (items, parentId = null) => {
      items.forEach((item) => {
        if (!item) return;
        if (parentId && item.id) {
          parentsMap[item.id] = parentId;
        }
        if (item.children) {
          registerParents(item.children, item.id);
        }
      });
    };
    registerParents(menuItems);
    return parentsMap;
  }, [menuItems]);

  const activeSubmenuAncestors = useMemo(() => {
    const ancestors = new Set();
    if (!activeTab) {
      return ancestors;
    }
    let current = activeTab;
    while (submenuParents[current]) {
      const parentId = submenuParents[current];
      ancestors.add(parentId);
      current = parentId;
    }
    return ancestors;
  }, [activeTab, submenuParents]);

  const clearSubmenuTimer = useCallback((submenuId) => {
    const timers = submenuTimersRef.current;
    if (!timers[submenuId]) {
      return;
    }
    clearTimeout(timers[submenuId]);
    delete timers[submenuId];
  }, []);

  const clearAllSubmenuTimers = useCallback(() => {
    Object.keys(submenuTimersRef.current).forEach((submenuId) => {
      clearSubmenuTimer(submenuId);
    });
  }, [clearSubmenuTimer]);

  useEffect(() => {
    if (searchTerm.trim()) {
      clearAllSubmenuTimers();
      return;
    }

    Object.keys(submenuTimersRef.current).forEach((submenuId) => {
      if (!openSubmenus[submenuId] || activeSubmenuAncestors.has(submenuId)) {
        clearSubmenuTimer(submenuId);
      }
    });

    Object.entries(openSubmenus).forEach(([submenuId, isOpen]) => {
      if (!isOpen || activeSubmenuAncestors.has(submenuId)) {
        clearSubmenuTimer(submenuId);
        return;
      }

      if (submenuTimersRef.current[submenuId]) {
        return;
      }

      submenuTimersRef.current[submenuId] = setTimeout(() => {
        setOpenSubmenus((prev) => {
          if (!prev[submenuId]) {
            return prev;
          }
          return { ...prev, [submenuId]: false };
        });
        clearSubmenuTimer(submenuId);
      }, 20000);
    });
  }, [
    openSubmenus,
    activeSubmenuAncestors,
    searchTerm,
    clearAllSubmenuTimers,
    clearSubmenuTimer,
  ]);

  useEffect(() => () => {
    clearAllSubmenuTimers();
  }, [clearAllSubmenuTimers]);

  const toggleSubmenu = useCallback((id) => {
    setOpenSubmenus((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const filteredMenuItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return menuItems;
    }

    const filterItems = (items) =>
      items
        .map((item) => {
          if (item.children) {
            const children = filterItems(item.children);
            const matches = item.label.toLowerCase().includes(term);
            if (matches || children.length) {
              return { ...item, children };
            }
            return null;
          }
          return item.label.toLowerCase().includes(term) ? item : null;
        })
        .filter(Boolean);

    return filterItems(menuItems);
  }, [menuItems, searchTerm]);

  const closeTab = useCallback(
    (id, options = {}) => {
      // Keep Dashboard and required password tab open.
      if (id === "home" || (user?.mustChangePassword && id === "profile")) return;
      setTabs((prev) => {
        const remaining = prev.filter((tab) => tab.id !== id);
        const fallbackId = options?.activateTabId;
        setActiveTab((currentActive) => {
          if (fallbackId && remaining.some((tab) => tab.id === fallbackId)) {
            return fallbackId;
          }
          if (currentActive === id) {
            return remaining.length ? remaining[remaining.length - 1].id : null;
          }
          return currentActive;
        });
        return remaining;
      });
    },
    [user?.mustChangePassword]
  );

  const closeAllTabs = useCallback(() => {
    setTabs((prev) => {
      const homeTab = prev.find((tab) => tab.id === "home");
      if (!homeTab) {
        setActiveTab(null);
        return [];
      }
      const refreshedHome = {
        ...homeTab,
        props: { ...(homeTab.props || {}), refreshKey: Date.now() },
      };
      if (user?.mustChangePassword) {
        const profileTab = prev.find((tab) => tab.id === "profile") || {
          id: "profile",
          title: "Perfil",
          Component: Profile,
          props: {},
        };
        const refreshedProfile = {
          ...profileTab,
          props: {
            ...(profileTab.props || {}),
            forcePasswordChange: true,
            refreshKey: Date.now(),
          },
        };
        setActiveTab("profile");
        return [refreshedHome, refreshedProfile];
      }
      setActiveTab("home");
      return [refreshedHome];
    });
  }, [user?.mustChangePassword]);

  const openRequestsTab = useCallback(() => {
    openTab("requests", "Minhas solicitações", Requests, {
      openTab,
      initialFilter: "pending",
    });
  }, [openTab]);

  const openRequestsWithNew = useCallback(() => {
    openTab("requests", "Minhas solicitações", Requests, {
      openTab,
      initialFilter: "pending",
      triggerNewRequest: Date.now(),
    });
  }, [openTab]);

  useEffect(() => {
    // openTab changes when the navigation switches between desktop and mobile.
    // Initialize once so resizing preserves the current module and its draft.
    if (initialHomeTabOpenedRef.current) return;
    initialHomeTabOpenedRef.current = true;
    openTab("home", "Dashboard", HomeDashboard, { openTab });
  }, [openTab]);

  useEffect(() => {
    if (typeof window === "undefined" || user?.mustChangePassword) return;
    let token = null;
    try {
      token = JSON.parse(window.sessionStorage.getItem(STUDENT_PRINT_NAVIGATION_STORAGE_KEY) || "null");
      const studentTab = String(token?.studentTab || "");
      const valid = token?.version === STUDENT_PRINT_NAVIGATION_VERSION && token?.state === "student-management:students" && token?.activeTab === "students" && /^(personal|family|authorizations|address|social|education|health|course|files|confidential|incidents|audit|custom-\d+)$/.test(studentTab) && /^\d+$/.test(String(token?.studentId || "")) && Number.isFinite(Number(token?.createdAt)) && Number.isFinite(Number(token?.expiresAt)) && Number(token?.createdAt) <= Date.now() && Number(token?.expiresAt) >= Date.now() && typeof token?.nonce === "string" && token.nonce.length > 0;
      if (!valid) { window.sessionStorage.removeItem(STUDENT_PRINT_NAVIGATION_STORAGE_KEY); token = null; }
    } catch (error) {
      window.sessionStorage.removeItem(STUDENT_PRINT_NAVIGATION_STORAGE_KEY);
    }
    if (!token) return;
    const studentItem = menuItems.find((item) => item.id === "student-management");
    if (studentItem?.component) {
      openTab(studentItem.id, studentItem.label, studentItem.component, studentItem.props || {});
    }
  }, [menuItems, openTab, user?.mustChangePassword]);

  useEffect(() => {
    if (typeof window === "undefined" || user?.mustChangePassword) return;
    let token = null;
    try {
      token = JSON.parse(window.sessionStorage.getItem(ATTENDEE_PRINT_NAVIGATION_STORAGE_KEY) || "null");
      const attendeeTab = String(token?.attendeeTab || "");
      const valid = token?.version === ATTENDEE_PRINT_NAVIGATION_VERSION && token?.state === "attendee-management:attendees" && token?.activeTab === "attendees" && /^(personal|family|authorizations|address|social|education|health|course|files|confidential|incidents|audit|custom-\d+)$/.test(attendeeTab) && /^\d+$/.test(String(token?.attendeeId || "")) && Number.isFinite(Number(token?.createdAt)) && Number.isFinite(Number(token?.expiresAt)) && Number(token?.createdAt) <= Date.now() && Number(token?.expiresAt) >= Date.now() && typeof token?.nonce === "string" && token.nonce.length > 0;
      if (!valid) { window.sessionStorage.removeItem(ATTENDEE_PRINT_NAVIGATION_STORAGE_KEY); token = null; }
    } catch (error) {
      window.sessionStorage.removeItem(ATTENDEE_PRINT_NAVIGATION_STORAGE_KEY);
    }
    if (!token) return;
    const attendeeItem = menuItems.find((item) => item.id === "attendee-management");
    if (attendeeItem?.component) {
      openTab(attendeeItem.id, attendeeItem.label, attendeeItem.component, attendeeItem.props || {});
    }
  }, [menuItems, openTab, user?.mustChangePassword]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleAssistantAction = (event) => {
      const action = event?.detail;
      if (!action || !action.type) {
        return;
      }
      if (action.type === ASSISTANT_ACTION_TYPES.CLOSE_ALL_TABS) {
        closeAllTabs();
        return;
      }
      if (action.type === ASSISTANT_ACTION_TYPES.CLOSE_TAB) {
        if (action.tabId) {
          closeTab(action.tabId);
        }
        return;
      }
      if (action.type === ASSISTANT_ACTION_TYPES.RELOAD_PAGE) {
        if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
          window.location.reload();
        }
        return;
      }
      if (action.type !== ASSISTANT_ACTION_TYPES.OPEN_TAB) {
        return;
      }
      const targetTabId = action.tabId;
      if (!targetTabId) {
        return;
      }
      const menuItem = menuItemLookup[targetTabId];
      if (!menuItem) {
        return;
      }
      const resolvedTitle = action.tabLabel || menuItem.tabLabel || menuItem.label || targetTabId;
      const mergedProps = {
        ...(menuItem.props || {}),
        ...(action.tabProps || {}),
      };
      openTab(targetTabId, resolvedTitle, menuItem.component, mergedProps);
    };
    window.addEventListener(ASSISTANT_ACTION_EVENT, handleAssistantAction);
    return () => {
      window.removeEventListener(ASSISTANT_ACTION_EVENT, handleAssistantAction);
    };
  }, [closeAllTabs, closeTab, menuItemLookup, openTab]);

  useEffect(() => {
    if (!activeTab) return;
    refreshTab(activeTab);
  }, [activeTab, refreshTab]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const query = window.matchMedia("(max-width: 1023px)");
    const handleChange = (event) => {
      setIsMobile(event.matches);
    };
    setIsMobile(query.matches);
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", handleChange);
      return () => {
        query.removeEventListener("change", handleChange);
      };
    }
    query.addListener(handleChange);
    return () => {
      query.removeListener(handleChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
    } else {
      setCollapsed(false);
    }
  }, [isMobile]);

  const previousOverflow = useRef("");

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const { body } = document;
    if (!isMobile) {
      body.style.overflow = previousOverflow.current || "";
      return undefined;
    }
    if (mobileMenuOpen) {
      previousOverflow.current = body.style.overflow;
      body.style.overflow = "hidden";
    } else {
      body.style.overflow = previousOverflow.current || "";
    }
    return () => {
      body.style.overflow = previousOverflow.current || "";
    };
  }, [isMobile, mobileMenuOpen]);

  const openProfileTab = useCallback(
    (options = {}) => {
      openTab("profile", "Perfil", Profile, {
        forcePasswordChange: Boolean(user?.mustChangePassword),
        ...options,
      });
    },
    [openTab, user?.mustChangePassword]
  );

  useEffect(() => {
    setAvatarError(false);
  }, [resolvedUserPhotoUrl]);

  const profileTrigger = (variant = "desktop") => {
    const isMobileVariant = variant === "mobile";
    const showPhoto = Boolean(resolvedUserPhotoUrl) && !avatarError;
    const sizeClass = isMobileVariant ? "h-10 w-10" : "h-9 w-9";

    return (
      <button
        type="button"
        onClick={() => openProfileTab()}
        className={`flex items-center justify-center rounded-full border p-0.5 shadow-inner transition focus:outline-none focus:ring-2 ${
          isDarkTheme
            ? "border-slate-700 bg-slate-900 hover:border-slate-500 focus:ring-slate-600 focus:ring-offset-2 focus:ring-offset-slate-950"
            : "border-blue-200/80 bg-white/80 hover:border-blue-300 focus:ring-blue-200 focus:ring-offset-2 focus:ring-offset-white"
        } ${
          isMobileVariant ? "h-11 w-11" : ""
        }`}
        aria-label="Abrir perfil"
      >
        {showPhoto ? (
          <img
            src={resolvedUserPhotoUrl}
            alt="avatar"
            onError={() => setAvatarError(true)}
            className={`${sizeClass} rounded-full object-cover`}
          />
        ) : (
          <div
            className={`${sizeClass} flex items-center justify-center rounded-full bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 text-blue-500`}
          >
            <FaUser className={isMobileVariant ? "text-2xl" : "text-xl"} />
          </div>
        )}
      </button>
    );
  };

  useEffect(() => {
    if (user?.mustChangePassword) {
      if (!forcedProfileHandled) {
        openProfileTab({ forcePasswordChange: true });
        setForcedProfileHandled(true);
      }
    } else if (forcedProfileHandled) {
      setForcedProfileHandled(false);
    }
  }, [forcedProfileHandled, openProfileTab, user?.mustChangePassword]);

  return (
    <div
      className="dashboard-shell dashboard-theme-light flex min-h-screen bg-white text-slate-800"
    >
      {showTasksWelcomeLoader && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-hidden bg-[#03122f] px-6 text-white">
          <style>{`
            @keyframes nash-welcome-caret { 0%, 45% { opacity: 1; } 46%, 100% { opacity: 0; } }
            @keyframes nash-welcome-float { 0%, 100% { transform: translate3d(0, 0, 0) scale(1); } 50% { transform: translate3d(0, -12px, 0) scale(1.03); } }
          `}</style>
          <div aria-hidden="true" className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
          <div aria-hidden="true" className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-sky-400/20 blur-3xl" />
          <div className="relative flex max-w-3xl flex-col items-center text-center">
            <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-[2rem] border border-white/15 bg-white/10 shadow-2xl backdrop-blur" style={{ animation: 'nash-welcome-float 3s ease-in-out infinite' }}>
              <img src="/nashlogo.png" alt="Logo da Nash" className="h-16 w-16" />
            </div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.45em] text-blue-200/80">Carregando suas tarefas</p>
            <h1 className="min-h-[4rem] text-3xl font-semibold leading-tight text-white sm:text-5xl">
              <span>{typedWelcomeMessage}</span>
              <span className="ml-1 inline-block h-8 w-1 translate-y-1 rounded-full bg-sky-300 sm:h-11" style={{ animation: 'nash-welcome-caret 1s steps(1, end) infinite' }} />
            </h1>
            <div className="mt-10 h-1.5 w-64 overflow-hidden rounded-full bg-white/10" role="progressbar" aria-label="Carregando tarefas" aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(tasksWelcomeProgress)}>
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-300 via-sky-300 to-cyan-200 transition-[width] duration-500 ease-out"
                style={{ width: `${tasksWelcomeProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}
      {isMobile && mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        id={isMobile ? "dashboard-mobile-menu" : undefined}
        className={`${
          collapsed ? "md:w-20" : "md:w-72"
        } ${
          isMobile
            ? `fixed inset-y-0 left-0 z-40 flex w-72 max-w-full transform flex-col justify-between border-r ${
                isDarkTheme ? "border-slate-950 shadow-none" : "border-white/10 shadow-2xl"
              } bg-gradient-to-b from-[#07174c] via-[#061136] to-[#050a21] text-blue-50 transition-transform duration-300 ${
                mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
              }`
            : `relative z-20 flex flex-col justify-between border-r ${
                isDarkTheme ? "border-slate-950 shadow-none" : "border-white/10 shadow-2xl"
              } bg-gradient-to-b from-[#07174c] via-[#061136] to-[#050a21] text-blue-50 transition-all duration-300`
        }`}
      >
        <div
          className={`flex flex-col space-y-6 ${
            collapsed ? "px-2" : "px-4"
          } py-6 ${
            isMobile
              ? "pt-12 flex-1 overflow-y-auto overscroll-contain min-h-0"
              : ""
          }`}
        >
          <div className="flex items-center justify-between">
            <div
              className={`flex items-center ${
                collapsed ? "justify-center" : "space-x-3 text-lg font-semibold"
              }`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 shadow-inner">
                <img src="/nashlogo.png" alt="Logo da Nash" className="h-10 w-10" />
              </div>
              {!collapsed && (
                <span className="tracking-wide text-blue-50">Nash iBPM</span>
              )}
            </div>
            <button
              className="rounded-xl bg-white/5 p-2 text-blue-100 transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
              onClick={() =>
                isMobile
                  ? setMobileMenuOpen(false)
                  : setCollapsed((prev) => !prev)
              }
              aria-label={
                isMobile
                  ? "Fechar menu"
                  : collapsed
                    ? "Expandir menu"
                    : "Recolher menu"
              }
            >
              {isMobile ? (
                <FaChevronLeft />
              ) : collapsed ? (
                <FaChevronRight />
              ) : (
                <FaChevronLeft />
              )}
            </button>
          </div>

          {!collapsed && (
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-blue-100/60" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar no menu"
                className="w-full rounded-2xl border border-white/10 bg-white/10 py-2 pl-11 pr-4 text-sm text-blue-50 placeholder:text-blue-100/50 focus:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="Buscar itens do menu"
              />
            </div>
          )}

          <button
            type="button"
            onClick={openRequestsWithNew}
            title="Nova solicitação"
            aria-label="Abrir nova solicitação"
            className={`group flex w-full items-center rounded-2xl bg-gradient-to-r from-blue-600 via-sky-500 to-sky-400 text-sm font-semibold text-white shadow-lg transition hover:from-blue-700 hover:via-sky-600 hover:to-sky-500 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-transparent ${
              collapsed
                ? "justify-center gap-0 px-0 py-3"
                : "justify-start gap-3 px-4 py-3"
            }`}
          >
            <span
              className={`flex items-center justify-center rounded-2xl bg-white/20 text-lg shadow-inner transition ${
                collapsed ? "h-11 w-11" : "h-10 w-10"
              }`}
            >
              <FaPlusCircle />
            </span>
            {!collapsed && <span className="truncate">Nova solicitação</span>}
          </button>

          <nav
            className={`flex-1 space-y-1 ${
              isMobile ? "overflow-y-auto pr-1" : ""
            }`}
          >
            {filteredMenuItems.map((item) => {
              if (item.children) {
                const Icon = item.icon;
                const isOpen = openSubmenus[item.id];
                return (
                  <div key={item.id} className="space-y-1">
                    <button
                      onClick={() => toggleSubmenu(item.id)}
                      title={item.label}
                      className={`group relative flex w-full items-center rounded-2xl py-2 text-sm font-medium transition-all duration-200 ${
                        collapsed
                          ? "justify-center gap-0 px-1"
                          : "justify-start gap-3 px-3"
                      } ${
                        isOpen
                          ? "bg-white/15 text-white shadow-lg backdrop-blur"
                          : "text-blue-100/80 hover:bg-white/5 hover:text-white focus-visible:bg-white/10"
                      }`}
                      aria-expanded={isOpen}
                      aria-controls={`submenu-${item.id}`}
                    >
                      <span
                        className={`flex items-center justify-center rounded-2xl bg-white/10 text-base shadow-inner transition ${
                          collapsed ? "h-11 w-11" : "h-9 w-9"
                        } ${
                          isOpen
                            ? "bg-white/20 text-white"
                            : "group-hover:bg-white/15 group-hover:text-white"
                        }`}
                      >
                        <Icon />
                      </span>
                      {!collapsed && (
                        <span className="truncate" data-testid={`menu-${item.id}`}>
                          {item.label}
                        </span>
                      )}
                      {!collapsed && (
                        <span
                          className={`ml-auto transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        >
                          <FaChevronDown className="text-xs" />
                        </span>
                      )}
                    </button>
                    <div
                      id={`submenu-${item.id}`}
                      className={`${
                        isOpen ? "max-h-screen" : "max-h-0"
                      } space-y-1 overflow-hidden transition-all duration-300 ease-in-out`}
                    >
                      {item.children.map(({
                        id,
                        label,
                        tabLabel,
                        icon: ChildIcon,
                        component,
                        props = {},
                      }) => {
                        const isActive = activeTab === id;
                        const shouldUseExtraRoundedCorners =
                          id === "home" ||
                          id === "registration-center" ||
                          id === "registration-center-group";
                        const resolvedTitle = tabLabel || label;
                        return (
                          <button
                            key={id}
                            onClick={() => openTab(id, resolvedTitle, component, props)}
                            title={label}
                            className={`group relative flex w-full items-center ${
                              shouldUseExtraRoundedCorners
                                ? "rounded-3xl"
                                : "rounded-2xl"
                            } py-2 text-sm font-medium transition-all duration-200 ${
                              collapsed
                                ? "justify-center gap-0 px-1"
                                : "justify-start gap-3 pl-12 pr-3"
                            } ${
                              isActive
                                ? "bg-white/15 text-white shadow-lg backdrop-blur"
                                : "text-blue-100/80 hover:bg-white/5 hover:text-white focus-visible:bg-white/10"
                            }`}
                          >
                            <span
                              className={`flex items-center justify-center ${
                                shouldUseExtraRoundedCorners
                                  ? "rounded-3xl"
                                  : "rounded-2xl"
                              } bg-white/10 text-base shadow-inner transition ${
                                collapsed ? "h-11 w-11" : "h-9 w-9"
                              } ${
                                isActive
                                  ? "bg-white/20 text-white"
                                  : "group-hover:bg-white/15 group-hover:text-white"
                              }`}
                            >
                              <ChildIcon />
                            </span>
                            {!collapsed && (
                              <span className="truncate" data-testid={`menu-${id}`}>
                                {label}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              const { id, label, tabLabel, icon: Icon, component, props = {} } = item;
              const isActive = activeTab === id;
              const shouldUseExtraRoundedCorners =
                id === "home" ||
                id === "registration-center" ||
                id === "registration-center-group";
              const resolvedTitle = tabLabel || label;
              return (
                <button
                  key={id}
                  onClick={() => openTab(id, resolvedTitle, component, props)}
                  title={label}
                  className={`group relative flex w-full items-center ${
                    shouldUseExtraRoundedCorners ? "rounded-3xl" : "rounded-2xl"
                  } py-2 text-sm font-medium transition-all duration-200 ${
                    collapsed
                      ? "justify-center gap-0 px-1"
                      : "justify-start gap-3 px-3"
                  } ${
                    isActive
                      ? "bg-white/15 text-white shadow-lg backdrop-blur"
                      : "text-blue-100/80 hover:bg-white/5 hover:text-white focus-visible:bg-white/10"
                  }`}
                >
                  <span
                    className={`flex items-center justify-center ${
                      shouldUseExtraRoundedCorners ? "rounded-3xl" : "rounded-2xl"
                    } bg-white/10 text-base shadow-inner transition ${
                      collapsed ? "h-11 w-11" : "h-9 w-9"
                    } ${
                      isActive
                        ? "bg-white/20 text-white"
                        : "group-hover:bg-white/15 group-hover:text-white"
                    }`}
                  >
                    <Icon />
                  </span>
                  {!collapsed && (
                    <span className="truncate" data-testid={`menu-${id}`}>
                      {label}
                    </span>
                  )}
                </button>
              );
            })}
            {!filteredMenuItems.length && (
              <p className="px-3 text-xs text-blue-100/70">
                Nenhum item encontrado.
              </p>
            )}
          </nav>
        </div>
        <div className={`${collapsed ? "px-2" : "px-4"} pb-6`}>
          <div className="sticky bottom-6">
            <button
              onClick={logout}
              title="Sair"
              className={`${
                collapsed
                  ? "mx-auto flex h-11 w-11 items-center justify-center"
                  : "flex w-full items-center gap-3"
              } rounded-2xl bg-white/10 px-3 py-2 text-sm font-medium text-white transition hover:bg-white/20 hover:shadow-lg`}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white/15 shadow-inner">
                <FaPowerOff className="text-lg" />
              </span>
              {!collapsed && <span>Sair</span>}
            </button>
          </div>
        </div>
      </aside>
      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        <div
          className={`pointer-events-none absolute -left-32 top-24 h-64 w-64 rounded-full blur-3xl ${
            isDarkTheme ? "bg-indigo-900/40" : "bg-slate-200/30"
          }`}
        />
        <div
          className={`pointer-events-none absolute -right-20 bottom-10 h-72 w-72 rounded-full blur-3xl ${
            isDarkTheme ? "bg-cyan-900/30" : "bg-slate-200/20"
          }`}
        />
        <main className="relative flex-1 overflow-x-hidden p-4 sm:p-6">
          <header className="mb-4 flex items-center justify-between md:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl border shadow-md transition focus:outline-none focus:ring-2 ${
                isDarkTheme
                  ? "border-slate-700 bg-slate-900 text-slate-100 hover:border-slate-500 hover:text-white focus:ring-slate-500"
                  : "border-slate-200/80 bg-white/80 text-slate-700 hover:border-blue-300 hover:text-blue-600 focus:ring-blue-300"
              }`}
              aria-expanded={mobileMenuOpen}
              aria-controls="dashboard-mobile-menu"
              aria-label="Abrir menu"
            >
              <FaChevronRight />
            </button>
            <div
              className={`flex items-center gap-3 rounded-3xl border px-3 py-2 text-sm font-semibold shadow ${
                isDarkTheme
                  ? "border-slate-700 bg-slate-900/90 text-slate-100"
                  : "border-slate-200/60 bg-white/70 text-slate-700"
              }`}
            >
              <span className="uppercase tracking-wide">Nash iBPM</span>
              {profileTrigger("mobile")}
            </div>
          </header>
          {user?.mustChangePassword && (
            <div className="mb-4 rounded-3xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 shadow">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-amber-200 text-amber-500">
                    <FaExclamationTriangle />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-500">
                      Atenção necessária
                    </p>
                    <p className="text-sm text-amber-700">
                      Por segurança, defina uma nova senha para continuar utilizando todos os recursos da plataforma.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openProfileTab({ forcePasswordChange: true })}
                  className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-700 shadow-sm transition hover:border-amber-400 hover:text-amber-800"
                >
                  Atualizar senha agora
                </button>
              </div>
            </div>
          )}

          <Tabs
            isDarkTheme={isDarkTheme}
            tabs={tabs}
            active={activeTab}
            onSelect={handleSelectTab}
            onClose={closeTab}
            onCloseAll={closeAllTabs}
            extra={
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={closeAllTabs}
                  aria-label="Fechar todas as abas"
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm shadow-sm transition focus:outline-none focus-visible:ring-2 ${
                    isDarkTheme
                      ? "border-slate-700 bg-slate-900 text-slate-100 hover:border-slate-500 hover:text-white focus-visible:ring-slate-500"
                      : "border-blue-200/70 bg-white/80 text-blue-700 hover:border-blue-300 hover:text-blue-800 focus-visible:ring-blue-300"
                  }`}
                >
                  <FaSyncAlt className="text-base" />
                </button>
                <NotificationsBell
                  items={requestNotifications}
                  unreadCount={unreadNotifications}
                  onOpenRequests={openRequestsTab}
                  onMarkItemsRead={markNotificationsAsRead}
                  onRefresh={refreshNotifications}
                />
              </div>
            }
          />
          {!tabs.length && (
            <p className={isDarkTheme ? "text-slate-300" : "text-gray-600"}>
              Selecione um item no menu.
            </p>
          )}
        </main>
        <Suspense fallback={null}>{chatReady && <NashChatWidget />}</Suspense>
      </div>
    </div>
  );
}
